import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LeadDistributionRoute, ProtectedRoute, RoleRoute } from './auth/AuthContext.js';
import { CallbackPage, SilentRenewPage } from './auth/CallbackPage.js';
import { Layout } from './components/Layout.js';
import { LandingPage } from './pages/LandingPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { WorkspacePage } from './pages/WorkspacePage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { ContactDetailPage } from './pages/ContactDetailPage.js';
import { ConversationsPage } from './pages/ConversationsPage.js';
import { ConversationDetailPage } from './pages/ConversationDetailPage.js';
import { ResellerPortalPage } from './pages/ResellerPortalPage.js';
import { ApiKeysPage } from './pages/ApiKeysPage.js';
import { SellersPage } from './pages/admin/SellersPage.js';
import { BrandsPage } from './pages/admin/BrandsPage.js';
import { SitesPage } from './pages/admin/SitesPage.js';
import { PhoneNumbersPage } from './pages/admin/PhoneNumbersPage.js';
import { LeadDistributionPage } from './pages/admin/LeadDistributionPage.js';

function adminOnly(element: ReactNode) {
  return <RoleRoute roles={['platform_admin']}>{element}</RoleRoute>;
}

export const router = createBrowserRouter([
  {
    // Public marketing landing page with the TIPSSSO portal login in its navbar.
    path: '/welcome',
    element: <LandingPage />,
  },
  {
    path: '/auth/callback',
    element: <CallbackPage />,
  },
  {
    path: '/auth/silent-renew',
    element: <SilentRenewPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'workspace', element: <WorkspacePage /> },
      { path: 'contacts', element: <ContactsPage /> },
      { path: 'contacts/:id', element: <ContactDetailPage /> },
      { path: 'conversations', element: <ConversationsPage /> },
      { path: 'conversations/:id', element: <ConversationDetailPage /> },
      { path: 'settings/api-keys', element: <ApiKeysPage /> },
      {
        path: 'reseller',
        element: (
          <RoleRoute roles={['reseller_admin', 'reseller_user', 'platform_admin']}>
            <ResellerPortalPage />
          </RoleRoute>
        ),
      },
      {
        path: 'sales/lead-distribution',
        element: (
          <LeadDistributionRoute>
            <LeadDistributionPage />
          </LeadDistributionRoute>
        ),
      },
      { path: 'admin/sellers', element: adminOnly(<SellersPage />) },
      { path: 'admin/brands', element: adminOnly(<BrandsPage />) },
      { path: 'admin/sites', element: adminOnly(<SitesPage />) },
      { path: 'admin/phone-numbers', element: adminOnly(<PhoneNumbersPage />) },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
